// A project's register: Jira issues ∪ brain actions, banded by workstream,
// drawn with the standard table pattern (RegisterTable).
//
// Backlog is hidden by default. Shelley has 98 open issues and 12 actually in
// flight; Lewis has 100+. A card that lists every open issue cannot be walked in
// a standup, so the default view is "not exactly Backlog" and a footer reveals
// the rest. That state is per-card and deliberately not persisted — every
// standup starts from the in-flight view.

import { useEffect, useMemo, useState, type DragEvent } from "react";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../design/brainTheme";
import {
  updateProjectAction,
  type ProjectAction,
  type ProjectJiraIssueRow,
} from "../lib/brain-api";
import { CLIENT_STATUS_LANES } from "../pages/mui/actionGrouping";
import {
  InlineSelect,
  JiraAssigneeSelect,
  JiraTransitionSelect,
  type ActionRegisterPerson,
} from "./ActionRegisterTable";
import { AddWorkBar, type AddWorkCreated } from "./AddWorkBar";
import { deriveActionStatus, JIRA_BROWSE } from "./actionRegisterStatus";
import {
  buildRegisterRows,
  groupRegisterRows,
  matchesRegisterFilters,
  registerCounts,
  registerMetaLine,
  sortRegisterRows,
  UNGROUPED_BAND,
  type RegisterRow,
  type RegisterSortDir,
  type RegisterSortKey,
} from "./registerRows";
import {
  AssigneeMonogram,
  PriorityPill,
  PullCircle,
  RefCell,
  RegisterTable,
  RowAction,
  StatusDot,
  type RegisterTableColumn,
  type RegisterTableRow,
  type SpineTone,
} from "./RegisterTable";

const c = brainTokens.color;

const STATUS_OPTIONS = ["Not raised", "Raised", ...CLIENT_STATUS_LANES, "Dismiss"] as const;

/**
 * Open / Done / All, or `status:<Jira status label>` for a swimlane chip.
 * Coarse buckets keep reserved keys; status chips are prefixed so a Jira
 * status literally named "Open" cannot collide.
 */
export type RegisterDoneFilter = string;

/** Person tab only: the `+` circle that pulls a row into the Today rail. */
const PULL_COLUMN: RegisterTableColumn = { key: "pull", label: "", width: "34px", align: "center" };

function buildColumns(opts: {
  showOrganization: boolean;
  pull: boolean;
  assignable: boolean;
}): RegisterTableColumn[] {
  const cols: RegisterTableColumn[] = [
    // Wide enough for Jira's own words ("Highest"), not just P1/P2/P3.
    { key: "priority", label: "Pri", width: "82px", sortable: true },
    { key: "ref", label: "Ref", width: "108px" },
  ];
  if (opts.showOrganization) {
    cols.push({ key: "organization", label: "Customer", width: "120px", collapse: true, sortable: true });
  }
  cols.push(
    { key: "summary", label: "Summary", width: "minmax(0,1fr)" },
    { key: "status", label: "Status", width: "158px", collapse: true, sortable: true },
    // Wide enough for the monogram + the select caret when it is editable.
    { key: "assignee", label: "", width: opts.assignable ? "58px" : "26px", align: "center" },
  );
  if (opts.pull) cols.push(PULL_COLUMN);
  return cols;
}

/**
 * Pulling a row into today (handoff §6.3). Supplied only by the person tab —
 * the global Projects tab has no itinerary, so it passes nothing and the column
 * is not rendered at all.
 */
export interface RegisterPull {
  isPinned: (row: RegisterRow) => boolean;
  onToggle: (row: RegisterRow) => void;
  /**
   * Pull everything currently on screen in one write. The filter chips are the
   * selection mechanism — narrow to the statuses you mean, then take the lot,
   * instead of clicking sixty `+` circles. Omit to hide the button.
   */
  onPullMany?: (rows: RegisterRow[]) => void;
  /** Drag onto the rail; the rail draws a dashed ghost at the landing spot. */
  onDragStart?: (row: RegisterRow) => void;
  onDragEnd?: () => void;
  /** Hovering a row previews the same ghost, so the gesture is discoverable. */
  onHover?: (row: RegisterRow | null) => void;
  busy?: boolean;
}

/** The row's condition → the colour of its 3px left spine. */
function spineFor(row: RegisterRow): SpineTone {
  if (row.done) return "muted";
  if (row.flagged) return "danger";
  if (row.priority === "P1") return "danger";
  if (row.priority === "P2") return "warning";
  if (row.kind === "action" && !row.issueKey) return "neutral";
  return "teal";
}

/** The status choice an action-backed row currently shows in the editor. */
function statusChoice(a: ProjectAction): string {
  if (a.status === "dismissed") return "Dismiss";
  if (a.status === "done") return "Done";
  if (a.clientStatus) return String(a.clientStatus);
  return a.jiraIssueKey ? "Raised" : "Not raised";
}

function statusPatch(a: ProjectAction, choice: string): Parameters<typeof updateProjectAction>[1] {
  if (choice === "Dismiss") return { status: "dismissed" };
  if (choice === "Done") return { status: "done", clientStatus: "Done" };
  if (choice === "Not raised" || choice === "Raised") {
    return { status: a.status === "done" || a.status === "dismissed" ? "captured" : a.status, clientStatus: null };
  }
  return { status: a.status === "done" || a.status === "dismissed" ? "captured" : a.status, clientStatus: choice };
}

/**
 * A workstream is a HEADER BLOCK you click, not a caption. It used to be mono
 * micro-type on the panel background — the same grey on nearly the same grey —
 * which made the one thing you navigate by the hardest thing to see. It now has
 * its own surface, ink-weight type, and it owns the open/closed state of its
 * rows: a project with eight workstreams opens as eight legible headings, not
 * two hundred rows.
 */
function BandHeader({
  band,
  counts,
  open,
  onToggle,
}: {
  band: string;
  counts: { inFlight: number; total: number };
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-expanded={open}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        width: "calc(100% - 24px)",
        mx: "12px",
        mt: 1.25,
        mb: open ? 0.5 : 0,
        p: "9px 12px",
        border: `1px solid ${open ? c.tealTintBorder : c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        background: open ? c.tealTint2 : c.white,
        cursor: "pointer",
        textAlign: "left",
        font: "inherit",
        "&:hover": { borderColor: c.hoverBorder },
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          fontSize: 11,
          color: open ? c.tealInk : c.muted2,
          transform: open ? "rotate(90deg)" : "none",
          transition: "transform 0.15s ease",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        ▸
      </Box>
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 13.5,
          fontWeight: 650,
          letterSpacing: "-0.01em",
          color: open ? c.tealInk : c.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {band === UNGROUPED_BAND ? "Ungrouped" : band}
      </Box>
      <Box
        component="span"
        sx={{
          flex: "none",
          fontFamily: brainTokens.font.mono,
          fontSize: 11,
          color: counts.inFlight ? c.ink3 : c.faint,
        }}
      >
        {counts.inFlight} of {counts.total}
      </Box>
    </Box>
  );
}

/** Extra workstream names on a row claimed by more than one scope. */
function ExtraBands({ names }: { names: string[] }) {
  if (!names.length) return null;
  return (
    <Box
      component="span"
      title={names.join(" · ")}
      sx={{
        flex: "none",
        ml: 1,
        px: "6px",
        py: "1px",
        borderRadius: `${brainTokens.radius.chip}px`,
        background: c.tealTint2,
        color: c.tealInk,
        border: `1px solid ${c.tealTintBorder}`,
        fontFamily: brainTokens.font.mono,
        fontSize: 10,
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 160,
      }}
    >
      +{names.length === 1 ? ` ${names[0]}` : ` ${names.length} more`}
    </Box>
  );
}

export function ProjectRegister({
  projectId,
  projectName,
  issues,
  actions,
  bandOrder = [],
  truncated = false,
  nzDate = "",
  people = [],
  canEdit = false,
  canQuickAdd = false,
  defaultReleaseVersion,
  defaultBoard,
  onActionsChange,
  onIssuesChange,
  onRowClick,
  pull,
  rowFilter,
  /** Open | Done | All | status:<label>, or several OR-ed together. */
  doneFilter = "all",
  /** @deprecated prefer doneFilter="open"; kept for callers still passing hideDone. */
  hideDone = false,
  /** Support desk: show JSM Organization as a Customer column. */
  showOrganization = false,
  emptyText = "No issues or actions on this project yet.",
}: {
  projectId?: string;
  /** Display name for the register AddWorkBar project chip (single-project cards). */
  projectName?: string;
  /** Live Jira rows from `/admin/projects/{id}/jira-issues` (already deduped). */
  issues?: ProjectJiraIssueRow[] | null;
  actions?: ProjectAction[] | null;
  /** Scope names, so bands read in the order the project declares them. */
  bandOrder?: string[];
  /** A scope ran past the server's page cap — say so rather than imply totality. */
  truncated?: boolean;
  /** @deprecated QuickAddRow leftover; unused by AddWorkBar. */
  nzDate?: string;
  /** @deprecated QuickAddRow leftover; unused by AddWorkBar. */
  people?: ActionRegisterPerson[];
  canEdit?: boolean;
  canQuickAdd?: boolean;
  /** @deprecated QuickAddRow leftover; unused by AddWorkBar. */
  defaultReleaseVersion?: string;
  /** Jira project key for create — defaults the Board field. */
  defaultBoard?: string;
  onActionsChange?: (next: ProjectAction[]) => void;
  /** Optimistic Jira status patches after register transitions. */
  onIssuesChange?: (next: ProjectJiraIssueRow[]) => void;
  onRowClick?: (row: RegisterRow) => void;
  /** Person tab: pull-into-today. Omitted everywhere else. */
  pull?: RegisterPull;
  /** Person tab: the `Aaron (3) / All rows` segmented filter. */
  rowFilter?: (row: RegisterRow) => boolean;
  doneFilter?: RegisterDoneFilter | RegisterDoneFilter[];
  hideDone?: boolean;
  showOrganization?: boolean;
  emptyText?: string;
}) {
  void nzDate;
  void people;
  void defaultReleaseVersion;
  const { enqueueSnackbar } = useSnackbar();
  const [showBacklog, setShowBacklog] = useState(false);
  const [sort, setSort] = useState<{ key: RegisterSortKey; dir: RegisterSortDir } | null>(null);
  // Local mirror so a transition can optimistically patch status without waiting
  // for the parent to re-fetch; sync whenever the parent props change.
  const [issueList, setIssueList] = useState<ProjectJiraIssueRow[] | null | undefined>(issues);
  useEffect(() => {
    setIssueList(issues);
  }, [issues]);

  // One filter or many — normalise to the multi-select form. "all" carries no
  // constraint, so it collapses to the empty selection.
  const effectiveDoneFilters: RegisterDoneFilter[] = useMemo(() => {
    const raw = Array.isArray(doneFilter) ? doneFilter : [doneFilter];
    const picked = raw.filter((f) => f && f !== "all");
    if (picked.length) return picked;
    return hideDone ? ["open"] : [];
  }, [doneFilter, hideDone]);

  // The filter is applied to the FULL row set, so the meta line, the band
  // counts and the `+ N backlog` reveal all describe the same rows the room is
  // looking at. Filtering only the rendered set would leave the counters
  // describing work the filter just hid.
  const rows = useMemo(() => {
    const built = buildRegisterRows({ issues: issueList, actions, projectId });
    return rowFilter ? built.filter(rowFilter) : built;
  }, [issueList, actions, rowFilter, projectId]);
  const counts = useMemo(() => registerCounts(rows), [rows]);
  const shown = useMemo(
    () =>
      rows.filter((r) => {
        if (!showBacklog && r.backlog) return false;
        return matchesRegisterFilters(r, effectiveDoneFilters);
      }),
    [rows, showBacklog, effectiveDoneFilters],
  );
  // What "pull everything showing" would actually take: the filtered set, minus
  // anything closed or already on the day. Backlog rows are excluded for free —
  // they are not in `shown` unless the reveal is open.
  const pullable = useMemo(
    () => (pull?.onPullMany ? shown.filter((r) => !r.done && !pull.isPinned(r)) : []),
    [shown, pull],
  );
  const bands = useMemo(() => {
    const grouped = groupRegisterRows(shown, bandOrder);
    if (!sort) return grouped;
    return grouped.map(({ band, rows: bandRows }) => ({
      band,
      rows: sortRegisterRows(bandRows, sort.key, sort.dir),
    }));
  }, [shown, bandOrder, sort]);
  // Workstreams start CLOSED — a card opens onto its shape, not 200 rows.
  // A register with a SINGLE band is not a shape worth hiding, so it opens; the
  // header still renders (and still collapses) because losing the workstream
  // name to save one click is the trade that made these registers unreadable.
  const [openBands, setOpenBands] = useState<Record<string, boolean>>({});
  const bandOpen = (band: string) => openBands[band] ?? bands.length === 1;
  // Nothing to name: no workstreams at all. Draw the rows bare.
  const nameless = bands.length === 1 && bands[0]!.band === UNGROUPED_BAND;
  // The pull circle is the LAST column, so the gesture lands in the same place
  // on every row of every card. Rendered only when the surface supports pinning.
  const columns = useMemo(
    () => buildColumns({ showOrganization, pull: !!pull, assignable: canEdit }),
    [showOrganization, pull, canEdit],
  );

  const onSortHeader = (key: string) => {
    if (key !== "priority" && key !== "status" && key !== "organization") return;
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: "asc" };
      return { key, dir: cur.dir === "asc" ? "desc" : "asc" };
    });
  };

  const publish = (next: ProjectAction[]) => onActionsChange?.(next);

  const appendCreated = (r: AddWorkCreated) => {
    if (r.kind === "jira") {
      const row: ProjectJiraIssueRow = {
        key: r.key,
        summary: r.summary ?? r.key,
        status: r.status ?? "To Do",
        statusCategory: "new",
        assignee: r.assignee ?? null,
        assigneeAccountId: null,
        priority: r.priority ?? null,
        updated: new Date().toISOString(),
        flagged: false,
        url: r.url,
        workStreams: bandOrder[0] ? [bandOrder[0]] : [],
        organization: null,
      };
      setIssueList((cur) => {
        const prev = cur ?? [];
        if (prev.some((i) => i.key === row.key)) return prev;
        const next = [row, ...prev];
        onIssuesChange?.(next);
        return next;
      });
      return;
    }
    // Note/question — append the real brain action (AddWorkBar already awaited create).
    publish([...(actions ?? []), r.action]);
  };

  const addBar =
    canQuickAdd && projectId ? (
      <Box sx={{ px: "12px", pt: 1.25, pb: 0.5 }}>
        <AddWorkBar
          scope="register"
          projectId={projectId}
          defaultBoard={defaultBoard}
          projects={[{ id: projectId, name: projectName || projectId, board: defaultBoard }]}
          onCreated={appendCreated}
        />
      </Box>
    ) : null;

  const patchIssueStatus = (
    key: string,
    next: { status: string; statusCategory?: string },
  ) => {
    setIssueList((cur) => {
      const prev = cur ?? [];
      const updated = prev.map((i) =>
        i.key === key
          ? {
              ...i,
              status: next.status,
              ...(next.statusCategory ? { statusCategory: next.statusCategory } : {}),
            }
          : i,
      );
      onIssuesChange?.(updated);
      return updated;
    });
  };

  const patchIssueAssignee = (
    key: string,
    next: { assignee: string | null; assigneeAccountId: string | null },
  ) => {
    setIssueList((cur) => {
      const prev = cur ?? [];
      const updated = prev.map((i) => (i.key === key ? { ...i, ...next } : i));
      onIssuesChange?.(updated);
      return updated;
    });
  };

  const patchAction = async (
    a: ProjectAction,
    patch: Parameters<typeof updateProjectAction>[1],
    optimistic: Partial<ProjectAction>,
  ) => {
    const prev = actions ?? [];
    publish(prev.map((x) => (x.id === a.id ? { ...x, ...optimistic } : x)));
    try {
      const updated = await updateProjectAction(a.id, patch);
      publish((actions ?? prev).map((x) => (x.id === a.id ? updated : x)));
    } catch (e) {
      publish(prev);
      enqueueSnackbar(e instanceof Error ? e.message : "Update failed.", { variant: "error" });
    }
  };

  const toTableRow = (row: RegisterRow): RegisterTableRow => {
    const action = row.actions[0];
    // A brain action merged onto a Jira row supplies context, never status —
    // the live Jira status is the truth for anything that reached Jira.
    const context = row.kind === "merged" ? action?.comments?.trim() || action?.text?.trim() : undefined;
    const clientStatus = action?.clientStatus ? String(action.clientStatus) : null;
    const extras = row.workStreams.slice(1);
    const pid = row.projectId || projectId || null;
    const jiraBacked = !!(row.issueKey && row.jiraStatus && pid);

    const statusCell =
      canEdit && jiraBacked ? (
        <RowAction>
          <JiraTransitionSelect
            projectId={pid!}
            issueKey={row.issueKey!}
            statusLabel={row.status.label}
            statusTone={row.status.tone}
            onOptimistic={(next) => {
              // Closures capture this render's row — revert uses the same origin.
              patchIssueStatus(row.issueKey!, {
                status: next.status,
                statusCategory: next.statusCategory ?? row.statusCategory ?? undefined,
              });
            }}
            onRevert={() => {
              if (!row.jiraStatus) return;
              patchIssueStatus(row.issueKey!, {
                status: row.jiraStatus,
                statusCategory: row.statusCategory ?? undefined,
              });
            }}
          />
        </RowAction>
      ) : canEdit && action && row.kind !== "jira" && !row.jiraStatus ? (
        <RowAction>
          <InlineSelect
            value={statusChoice(action)}
            width={150}
            options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
            renderValue={() => {
              const st = deriveActionStatus(action);
              return <StatusDot label={st.label} tone={st.tone} />;
            }}
            onChange={(choice) => {
              const patch = statusPatch(action, choice);
              void patchAction(action, patch, {
                status: (patch.status as ProjectAction["status"]) ?? action.status,
                clientStatus: (patch.clientStatus as string | undefined) ?? undefined,
              });
            }}
          />
        </RowAction>
      ) : (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <StatusDot label={row.status.label} tone={row.status.tone} />
          {clientStatus && clientStatus !== row.status.label && (
            <Box
              component="span"
              title={`Register: ${clientStatus}`}
              sx={{ fontFamily: brainTokens.font.mono, fontSize: 10, color: c.faint, whiteSpace: "nowrap" }}
            >
              · {clientStatus}
            </Box>
          )}
        </Box>
      );

    return {
      id: row.id,
      spine: spineFor(row),
      faded: row.done,
      title: `${row.issueKey ?? "No ref"} — ${row.summary}`,
      onClick: onRowClick ? () => onRowClick(row) : undefined,
      ...(pull
        ? {
            draggable: true,
            onDragStart: (e: DragEvent) => {
              // Firefox refuses to start a drag without data on the transfer.
              e.dataTransfer?.setData("text/plain", row.issueKey ?? row.id);
              if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy";
              pull.onDragStart?.(row);
            },
            onDragEnd: () => pull.onDragEnd?.(),
            onMouseEnter: () => pull.onHover?.(row),
            onMouseLeave: () => pull.onHover?.(null),
          }
        : {}),
      cells: {
        ...(pull
          ? {
              pull: (
                <PullCircle
                  pinned={pull.isPinned(row)}
                  busy={pull.busy}
                  label={row.summary}
                  onToggle={() => pull.onToggle(row)}
                />
              ),
            }
          : {}),
        priority: <PriorityPill priority={row.jiraPriority ?? row.priority} />,
        ref: (
          <RefCell
            label={row.issueKey}
            href={row.issueKey ? `${JIRA_BROWSE}/${encodeURIComponent(row.issueKey)}` : null}
          />
        ),
        ...(showOrganization
          ? {
              organization: (
                <Box
                  title={row.organization ?? undefined}
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: row.organization ? c.ink2 : c.placeholder,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.organization || "—"}
                </Box>
              ),
            }
          : {}),
        summary: (
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, width: "100%" }}>
            <Box
              title={context && context !== row.summary ? `${row.summary}\n${context}` : row.summary}
              sx={{
                fontSize: 13.5,
                fontWeight: 500,
                color: row.done ? c.muted : c.ink,
                letterSpacing: "-0.01em",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textDecoration: row.done ? "line-through" : "none",
                textDecorationColor: c.border,
              }}
            >
              {row.summary}
            </Box>
            <ExtraBands names={extras} />
          </Box>
        ),
        status: statusCell,
        assignee:
          canEdit && jiraBacked ? (
            <RowAction>
              <JiraAssigneeSelect
                projectId={pid!}
                issueKey={row.issueKey!}
                assignee={row.assignee}
                onOptimistic={(next) => patchIssueAssignee(row.issueKey!, next)}
                onRevert={() =>
                  patchIssueAssignee(row.issueKey!, {
                    assignee: row.assignee,
                    assigneeAccountId: row.assigneeAccountId,
                  })
                }
              />
            </RowAction>
          ) : (
            <AssigneeMonogram name={row.assignee} />
          ),
      },
    };
  };

  if (!rows.length) {
    return (
      <Box>
        <Box sx={{ px: "12px", py: 2, fontSize: 13, color: c.muted }}>{emptyText}</Box>
        {addBar}
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          px: "12px",
          pb: 1,
          fontFamily: brainTokens.font.mono,
          fontSize: 11.5,
          color: c.muted,
          letterSpacing: "0.02em",
        }}
      >
        {registerMetaLine(counts)}
        {truncated && (
          <Box
            component="span"
            title="A Jira scope returned more issues than the server fetches in one go. Counts are partial — open the board in Jira for the full set."
            sx={{
              px: "6px",
              py: "1px",
              borderRadius: `${brainTokens.radius.chip}px`,
              background: c.warnBg,
              color: c.warnText,
              fontSize: 10.5,
              letterSpacing: "0.04em",
              whiteSpace: "nowrap",
            }}
          >
            partial — Jira page cap
          </Box>
        )}
        {pull?.onPullMany && pullable.length > 0 && (
          <Box
            component="button"
            type="button"
            disabled={pull.busy}
            onClick={(e) => {
              e.stopPropagation();
              pull.onPullMany?.(pullable);
            }}
            title={`Pull all ${pullable.length} rows currently showing onto today`}
            sx={{
              ml: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              px: "9px",
              py: "3px",
              borderRadius: `${brainTokens.radius.pill}px`,
              border: `1px solid ${c.tealTintBorder}`,
              background: c.tealTint2,
              color: c.tealInk,
              fontFamily: brainTokens.font.sans,
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: 0,
              cursor: "pointer",
              "&:hover": { background: c.tealTint },
              "&:disabled": { opacity: 0.5, cursor: "default" },
            }}
          >
            + Pull {pullable.length} into today
          </Box>
        )}
      </Box>

      {bands.map(({ band, rows: bandRows }) => {
        const isOpen = nameless || bandOpen(band);
        return (
          <Box key={band}>
            {!nameless && (
              <BandHeader
                band={band}
                open={isOpen}
                onToggle={() => setOpenBands((m) => ({ ...m, [band]: !m[band] }))}
                counts={{
                  inFlight: bandRows.filter((r) => !r.done && !r.backlog).length,
                  total: bandRows.length,
                }}
              />
            )}
            {isOpen && (
              <RegisterTable
                columns={columns}
                rows={bandRows.map(toTableRow)}
                sortKey={sort?.key ?? null}
                sortDir={sort?.dir ?? "asc"}
                onSort={onSortHeader}
              />
            )}
          </Box>
        );
      })}

      {counts.backlog > 0 && (
        <Box sx={{ px: "12px", pt: 1.25 }}>
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowBacklog((v) => !v);
            }}
            sx={{
              border: "none",
              background: "none",
              p: 0,
              cursor: "pointer",
              fontFamily: brainTokens.font.mono,
              fontSize: 11.5,
              letterSpacing: "0.04em",
              color: c.tealInk,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {showBacklog ? `− hide ${counts.backlog} backlog` : `+ ${counts.backlog} backlog`}
          </Box>
        </Box>
      )}

      {addBar}
    </Box>
  );
}
